import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import AccessDenied from './components/AccessDenied';
import Layout from './components/Layout';
import LoginPage from './pages/Login';
import DashboardPage from './pages/Dashboard';
import LeadListPage from './pages/leads/LeadList';
import LeadNewPage from './pages/leads/LeadNew';
import LeadDetailPage from './pages/leads/LeadDetail';
import TeamPage from './pages/Team';
import NotificationsPage from './pages/Notifications';
import ReportsPage from './pages/Reports';
import ProfilePage from './pages/Profile';
import ObjectionsPage from './pages/admin/ObjectionsPage';
import StudentListPage  from './pages/students/StudentList';
import StudentDetailPage from './pages/students/StudentDetail';
import RegisterPage          from './pages/public/RegisterPage';
import FranchisePage         from './pages/funnel/FranchisePage';
import ApplyPage             from './pages/funnel/ApplyPage';
import PartnerWithUsPage     from './pages/funnel/PartnerWithUsPage';
import FunnelAnalyticsPage   from './pages/funnel/FunnelAnalyticsPage';
import FunnelPagesManager    from './pages/funnel/FunnelPagesManager';
import EmailSettingsPage     from './pages/settings/EmailSettingsPage';
import WhatsAppSettingsPage  from './pages/settings/WhatsAppSettingsPage';
import RevenuePage           from './pages/RevenuePage';
import CommissionPage        from './pages/CommissionPage';
import MyEarningsPage        from './pages/MyEarningsPage';
import ForgotPasswordPage    from './pages/ForgotPassword';
import ResetPasswordPage     from './pages/ResetPassword';
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt';
import KnowledgeBasePage     from './pages/knowledge/KnowledgeBase';
import TaskListPage          from './pages/tasks/TaskList';
import TaskDetailPage        from './pages/tasks/TaskDetail';
import TaskReportsPage       from './pages/tasks/TaskReports';

// Route-level role protection: roles/sub_roles that may access each path
const protectedRoutes = {
  '/tasks/reports':      ['super_admin', 'admin', 'MD', 'SVP', 'RCM', 'Manager'],
  '/revenue':            ['super_admin', 'admin', 'MD', 'SVP', 'RCM', 'Manager'],
  '/commission':         ['super_admin', 'admin'],
  '/funnel-analytics':   ['super_admin', 'admin'],
  '/funnel-pages':       ['super_admin', 'admin'],
  '/settings/email':     ['super_admin', 'admin'],
  '/settings/whatsapp':  ['super_admin', 'admin'],
  '/knowledge':          ['super_admin', 'admin'],
  '/team':               ['super_admin', 'admin'],
  '/admin/objections':   ['super_admin', 'admin'],
};

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f1f5f9' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '3px solid #C9A84C', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: '#1B2A6B', fontWeight: 600 }}>Loading Nova Era Labs CRM…</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;

  // Check route-level role protection
  const allowed = protectedRoutes[location.pathname];
  if (allowed) {
    const identifiers = [user.role, user.sub_role].filter(Boolean);
    const hasAccess = identifiers.some(id => allowed.includes(id));
    if (!hasAccess) return <AccessDenied />;
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  return (
    <>
      {user && <NotificationPermissionPrompt />}
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="leads" element={<LeadListPage />} />
        <Route path="leads/new" element={<LeadNewPage />} />
        <Route path="leads/:id" element={<LeadDetailPage />} />
        <Route path="team"               element={<TeamPage />} />
        <Route path="notifications"      element={<NotificationsPage />} />
        <Route path="reports"            element={<ReportsPage />} />
        <Route path="profile"            element={<ProfilePage />} />
        <Route path="admin/objections"   element={<ObjectionsPage />} />
        <Route path="students"           element={<StudentListPage />} />
        <Route path="students/:id"       element={<StudentDetailPage />} />
        <Route path="funnel-analytics"   element={<FunnelAnalyticsPage />} />
        <Route path="funnel-pages"       element={<FunnelPagesManager />} />
        <Route path="settings/email"     element={<EmailSettingsPage />} />
        <Route path="settings/whatsapp"  element={<WhatsAppSettingsPage />} />
        <Route path="revenue"            element={<RevenuePage />} />
        <Route path="commission"         element={<CommissionPage />} />
        <Route path="my-earnings"        element={<MyEarningsPage />} />
        <Route path="knowledge"          element={<KnowledgeBasePage />} />
        <Route path="tasks"              element={<TaskListPage />} />
        <Route path="tasks/reports"      element={<TaskReportsPage />} />
        <Route path="tasks/:id"          element={<TaskDetailPage />} />
      </Route>
      {/* Public routes — no auth required */}
      <Route path="/register"       element={<RegisterPage />} />
      <Route path="/register/:slug" element={<RegisterPage />} />
      <Route path="/franchise"         element={<FranchisePage />} />
      <Route path="/apply"             element={<ApplyPage />} />
      <Route path="/partner-with-us"   element={<PartnerWithUsPage />} />
      <Route path="/forgot-password"   element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" toastOptions={{ duration: 4000,
          style: { fontFamily: 'Inter, sans-serif', fontSize: 14 }
        }} />
      </BrowserRouter>
    </AuthProvider>
  );
}
